/*
* Reserve study report - inventory grid component
*/
import { Grid, GridCell, GridColumn as Column } from '@progress/kendo-react-grid';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import * as _ from 'lodash';
import * as React from 'react';
import { Row } from 'react-bootstrap';
import { FormattedNumber } from 'react-intl';

import * as AppUtilities from '../common/apputilities';
import { BootstrapColXl as Col } from '../common/bootstrap/bootstrapcolxl';
import { Formatters } from '../common/formatters';
import { GridUtilities } from '../common/grid/gridutilities';
import { WhiteBoxPageSection } from '../common/whiteboxpagesection';

const DebounceTime = 500; // Milliseconds

export class NumericInputCellBase extends GridCell {
    public constructor(props: any) {
        super(props);
    }

    public handleChange(event: any, val: any) {
        const { dataItem, field, onChange } = this.props;

        const onDirectItemChange = (this.props as any).onDirectItemChange;
        if (onDirectItemChange) {
            onDirectItemChange({
                dataItem: dataItem,
                field: field,
                value: val
            });
        }

        if (onChange) {
            onChange({
                dataItem: dataItem,
                field: field,
                syntheticEvent: event,
                value: val
            });
        }
    }
}

export class HypotheticalAvgCostCell extends NumericInputCellBase {
    public constructor(props: any) {
        super(props);
    }

    public render() {
        const { dataItem, field } = this.props;

        const editing = _.get(dataItem, 'editing', false);
        const value = _.get(dataItem, field as string, 0);

        let cellValue;
        const debounceFunc = _.debounce((e) => { super.handleChange(e.syntheticEvent.persist(), e.target.value); }, DebounceTime);

        if (editing) {

            cellValue = (
                <NumericTextBox
                    placeholder="Enter Avg Cost"
                    format="c2"
                    defaultValue={value}
                    step={100}
                    spinners={false}
                    onChange={debounceFunc}
                />
            );
        } else {
            cellValue = (
                <FormattedNumber value={value} style="currency" currency="USD" />
            );
        }

        return (
            <td className={`k-grid-column-avg-cost k-grid-numeric-input-avg-cost ${GridUtilities.KendoGridClassTextRight}`}>
                {cellValue}
            </td>
        );
    }
}

export class UsefulLifeCell extends NumericInputCellBase {
    public constructor(props: any) {
        super(props);
    }

    public render() {
        const { dataItem, field } = this.props;

        const editing = _.get(dataItem, 'editing', false);
        const value = _.get(dataItem, field as string, 0);

        let cellValue;
        const debounceFunc = _.debounce((e) => { super.handleChange(e.syntheticEvent.persist(), e.target.value); }, DebounceTime);
        if (editing) {
            cellValue = (
                <NumericTextBox
                    placeholder="Enter Years"
                    defaultValue={value}
                    spinners={false}
                    format="n0"
                    onChange={debounceFunc}
                />
            );
        } else {
            cellValue = (<span>{value}</span>);
        }

        return (
            <td className={`k-grid-column-useful-life k-grid-numeric-input-useful-life ${GridUtilities.KendoGridClassTextCenter}`}>
                {cellValue}
            </td>
        );
    }
}

export class IncludedCheckboxCell extends GridCell {
    public constructor(props: any) {
        super(props);
    }

    public handleChange(e: any) {
        const { dataItem, field, onChange } = this.props;

        const value = !_.get(dataItem, field as string, false);

        const onDirectItemChange = (this.props as any).onDirectItemChange;
        if (onDirectItemChange) {
            onDirectItemChange({
                dataItem: dataItem,
                field: field,
                value: value
            });
        }

        if (onChange) {
            onChange({
                dataItem: dataItem,
                field: field,
                syntheticEvent: e.syntheticEvent,
                value: value
            });
        }
    }

    public render() {
        const { dataItem, field } = this.props;

        const value = _.get(dataItem, field as string, 0);

        return (
            <td className={`k-grid-column-included k-grid-checkbox-input ${GridUtilities.KendoGridClassTextCenter}`}>
                <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => { if (this.handleChange) { this.handleChange(e); } }}
                />
            </td>
        );
    }
}

export class ComponentNameCell extends GridCell {
    public constructor(props: any) {
        super(props);
        this.handleScroll = this.handleScroll.bind(this);
    }

    private currentInputRef: HTMLInputElement | null = null;

    public componentDidMount() {
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('beforeunload', this.handleBeforeUnload);
        document.addEventListener('visibilitychange', this.handleVisibilityChange);
    }

    public componentWillUnmount() {
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('beforeunload', this.handleBeforeUnload);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    public handleScroll() {
        if (this.currentInputRef && document.activeElement === this.currentInputRef) {
            // User is scrolling while still focused on the input - save the change
            this.handleChange({ target: this.currentInputRef } as any);
        }
    }

    public handleBeforeUnload = () => {
        if (this.currentInputRef && document.activeElement === this.currentInputRef) {
            // User is leaving/refreshing page while input is focused - save the change
            this.handleChange({ target: this.currentInputRef } as any);
        }
    }

    public handleVisibilityChange = () => {
        if (this.currentInputRef && document.activeElement === this.currentInputRef && document.hidden) {
            // User switched tabs/apps while input is focused - save the change
            this.handleChange({ target: this.currentInputRef } as any);
        }
    }

    public handleChange(event: any) {
        const { dataItem, field, onChange } = this.props;
        const val = event.target.value;

        const onDirectItemChange = (this.props as any).onDirectItemChange;
        if (onDirectItemChange) {
            onDirectItemChange({
                dataItem: dataItem,
                field: field,
                value: val
            });
        }

        if (onChange) {
            onChange({
                dataItem: dataItem,
                field: field,
                syntheticEvent: event,
                value: val
            });
        }
    }

    public render() {
        const { field, dataItem, rowType } = this.props;
        const editing = _.get(dataItem, 'editing', false);
        const value = _.get(dataItem, field as string, '');

        let cellValue;

        if (editing) {
            cellValue = (
                <input
                    ref={(ref) => { this.currentInputRef = ref; }}
                    type="text"
                    className="k-textbox"
                    defaultValue={value}
                    onBlur={(e) => this.handleChange(e)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            this.handleChange(e);
                            (e.target as HTMLInputElement).blur();
                        }
                    }}
                />
            );
        } else {
            cellValue = (<span>{value}</span>);
        }

        return (
            <td className={`k-grid-column-comp-name ${GridUtilities.KendoGridClassTextLeft}`}>
                {cellValue}
            </td>
        );
    }
}

export const InventoryGridsSection = ({ gridTitlePrefix, inventory, onInventoryItemChange, inventorySubgroups, appTextGridColumnHeader, sectionTitle = 'Inventory', ...rest }) => {
    if (!AppUtilities.isArrayWithLength(inventory) || !appTextGridColumnHeader) { return (<div />); }

    const stateGridKey = _.get(rest, 'gridKey', '');

    const enableTracing: boolean = false;

    const rowRenderer = (trElement: React.ReactElement<any>, dataItem: any) => {
        const trProps = { ...trElement.props };

        const addClass = _.get(dataItem.dataItem, GridUtilities.KendoGridAddRowClassFieldName, '');
        if (addClass) { trProps.className += ` ${addClass}`; }

        return React.cloneElement(trElement, { ...trProps }, trElement.props.children);
    };

    const grids = _(inventory)
        .groupBy('subgroup')
        .toPairs()
        .sortBy(p => p[0])
        .map((p, ind) => {
            const k = p[0];
            const inv = p[1];

            const subgroup = _.find(inventorySubgroups, ['groupCode', k]);
            const prefix = (gridTitlePrefix) ? `${gridTitlePrefix}: ` : '';
            const appendMax = (k === 'XYZ') ? ` Maximum` : ``;
            const suffix = ` (${Formatters.plural('Item', inv.length, true)}${appendMax})`;
            const gridTitleCaption = `${prefix}${_.get(subgroup, 'title', k)}${suffix}`;
            const gridKey = `inventory-grid-section-ind${ind}-sgk${stateGridKey}`;

            if (enableTracing) { console.log(`inventorygrids.tsx::InventoryGridsSection ind ${ind} gridTitleCaption ${gridTitleCaption} gridKey ${gridKey}`, { inv, stateGridKey }); }

            return {
                title: gridTitleCaption,
                grid: (
                    <Grid
                        key={gridKey}
                        data={inv}
                        resizable={true}
                        editField="editing"
                        scrollable="none"
                        rowRender={rowRenderer}
                        // @ts-ignore
                        itemChange={(e) => {
                            if (onInventoryItemChange) {
                                onInventoryItemChange(e);
                            }
                        }}
                    >
                        {/* Component details */}
                        <Column field="componentId" title={appTextGridColumnHeader('id')} editable={false} headerClassName={`k-grid-column-comp-id ${GridUtilities.KendoGridClassTextLeft}`} />
                        <Column 
                            field="componentName" 
                            title={appTextGridColumnHeader('name')} 
                            editable={(k === 'XYZ') ? true : false}
                            {...((k === 'XYZ') ? { cell: (props) => <ComponentNameCell {...props as any} onDirectItemChange={onInventoryItemChange} /> } : {})}
                            headerClassName={`k-grid-column-comp-name ${GridUtilities.KendoGridClassTextLeft}`} 
                        />

                        {/* Your Values */}
                        {/* Include in calculations */}
                        <Column
                            field="included"
                            title={appTextGridColumnHeader('your-included')}
                            cell={(props) => <IncludedCheckboxCell {...props as any} onDirectItemChange={onInventoryItemChange} />}
                            headerClassName={`k-grid-column-included ${GridUtilities.KendoGridClassTextCenter}`}
                        />

                        { /* HYP FIELDS */}
                        <Column
                            field="hypAvgCost"
                            title={appTextGridColumnHeader('your-avg-cost')}
                            cell={(props) => <HypotheticalAvgCostCell {...props as any} onDirectItemChange={onInventoryItemChange} />}
                            headerClassName={`k-grid-column-avg-cost ${GridUtilities.KendoGridClassTextRight}`}
                        />
                        <Column
                            field="hypUsefulLife"
                            title={appTextGridColumnHeader('your-ul')}
                            cell={(props) => <UsefulLifeCell {...props as any} onDirectItemChange={onInventoryItemChange} />}
                            headerClassName={`k-grid-column-useful-life ${GridUtilities.KendoGridClassTextCenter}`}
                        />
                        <Column
                            field="hypRemainingUsefulLife"
                            title={appTextGridColumnHeader('your-rl')}
                            cell={(props) => <UsefulLifeCell {...props as any} onDirectItemChange={onInventoryItemChange} />}
                            headerClassName={`k-grid-column-useful-life ${GridUtilities.KendoGridClassTextCenter}`}
                        />

                        {/* Reserve Study Values */}
                        <Column
                            field="avgCost"
                            title={appTextGridColumnHeader('study-avg-cost')}
                            editable={false}
                            format="{0:c0}"
                            className={GridUtilities.KendoGridClassTextRight}
                            headerClassName={`k-grid-column-study-avg-cost ${GridUtilities.KendoGridClassTextRight}`}
                        />
                        <Column
                            field="usefulLife"
                            title={appTextGridColumnHeader('study-ul')}
                            editable={false}
                            className={GridUtilities.KendoGridClassTextCenter}
                            headerClassName={`k-grid-column-study-useful-life ${GridUtilities.KendoGridClassTextCenter}`}
                        />
                        <Column
                            field="remainingUsefulLife"
                            title={appTextGridColumnHeader('study-rl')}
                            editable={false}
                            className={GridUtilities.KendoGridClassTextCenter}
                            headerClassName={`k-grid-column-study-remaining-useful-life ${GridUtilities.KendoGridClassTextCenter}`}
                        />

                    </Grid>
                )
            };
        })
        .value();

    return (
        <div>
            <WhiteBoxPageSection title={sectionTitle}>
                {grids.map((c, i) => (
                    <Row className="inventory-grid-row" key={i}>
                        <Col xs={12}>
                            <h4 className="inventory-grid-row-title">{c.title}</h4>
                            {c.grid}
                        </Col>
                    </Row>)
                )}
            </WhiteBoxPageSection>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/report/inventorygrids.tsx